// Lote V-11.C — As miñas facturas. Lista cronolóxica de facturas
// recibidas polo usuario actual (como vendedor ou comprador).

import { Download, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
  getInvoiceDownloadUrl,
  listMyInvoices,
  markInvoiceRead,
  type InvoiceListItem,
} from '../api/invoices';
import { describeAxiosError } from '../api/errors';
import ContentPage from '../components/ui/ContentPage';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

function formatEuros(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('gl-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function InvoicesPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuthContext();
  const { addToast } = useToast();
  const [items, setItems] = useState<InvoiceListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    void listMyInvoices(page)
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total);
      })
      .catch((err) => {
        if (!cancelled) addToast(describeAxiosError(err, t('common.error')), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, page, addToast, t]);

  if (!accessToken) {
    return <Navigate to="/marketplace/login?next=/marketplace/facturas" replace />;
  }

  async function handleDownload(item: InvoiceListItem): Promise<void> {
    setDownloading(item.id);
    try {
      const url = await getInvoiceDownloadUrl(item.id);
      window.open(url, '_blank');
      if (!item.read_at) {
        await markInvoiceRead(item.id);
        setItems((prev) =>
          prev.map((x) => (x.id === item.id ? { ...x, read_at: new Date().toISOString() } : x)),
        );
      }
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido descargar a factura'), 'error');
    } finally {
      setDownloading(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <ContentPage
      title="As miñas facturas"
      intro="Aquí tes todas as facturas emitidas pola plataforma cara a túa conta."
      category="Conta"
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner size={16} /> {t('common.loading')}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="not-prose border border-border rounded-2xl p-8 text-center">
          <FileText size={36} className="mx-auto mb-3 text-muted" />
          <p className="text-sm font-medium text-dark">Aínda non tes facturas</p>
          <p className="text-xs text-muted mt-1">
            As facturas xeraranse automaticamente cando completes unha venda ou compra no
            marketplace.
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          <ul className="not-prose border border-border rounded-2xl bg-white divide-y divide-border overflow-hidden">
            {items.map((inv) => (
              <li key={inv.id} className="p-4 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-dark">
                      {inv.full_number}
                    </span>
                    {inv.invoice_type === 'RECTIFICATIVE' && (
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        Rectificativa
                      </span>
                    )}
                    {!inv.read_at && (
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-rauxa/10 text-rauxa">
                        Nova
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted mt-1">
                    {formatDate(inv.issued_at)} ·{' '}
                    {inv.recipient_role === 'SELLER' ? 'Como vendedor' : 'Como comprador'}
                  </p>
                  <p className="text-xs text-muted mt-0.5 truncate">{inv.concept}</p>
                </div>
                <div className="flex-none text-right">
                  <p className="text-base font-bold text-dark">{formatEuros(inv.total_cents)} €</p>
                  <p className="text-[11px] text-muted">Base {formatEuros(inv.base_cents)} + IVE</p>
                  <button
                    type="button"
                    onClick={() => handleDownload(inv)}
                    disabled={!inv.has_pdf || downloading === inv.id}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-rauxa hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!inv.has_pdf ? 'PDF aínda non dispoñible' : 'Descargar PDF'}
                  >
                    {downloading === inv.id ? <Spinner size={12} /> : <Download size={14} />}
                    PDF
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div className="not-prose mt-4 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="text-rauxa font-semibold disabled:text-muted"
              >
                ← Anterior
              </button>
              <span className="text-muted">
                Páxina {page} de {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="text-rauxa font-semibold disabled:text-muted"
              >
                Seguinte →
              </button>
            </div>
          )}
        </>
      )}
    </ContentPage>
  );
}
